import api from "./api";
import {
  IWebhookDocument,
  WebhookEntrega,
  WebhookAgenteVinculo,
} from "../types/webhook";
import { authService } from "./authService";

// Define interfaces for API responses
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Type for axios errors
interface AxiosError {
  response?: {
    status: number;
    statusText: string;
    data: any;
  };
  message?: string;
}

export class WebhookService {
  private static instance: WebhookService;
  private baseUrl: string;

  constructor() {
    // Usar URL relativa para funcionar com o baseURL do api.ts
    this.baseUrl = "/api/webhooks";
  }

  public static getInstance(): WebhookService {
    if (!WebhookService.instance) {
      WebhookService.instance = new WebhookService();
    }
    return WebhookService.instance;
  }

  /**
   * Cria um novo webhook
   * @param webhook Dados do webhook a ser criado
   * @returns Webhook criado
   */
  async criar(webhook: Partial<IWebhookDocument>): Promise<IWebhookDocument> {
    try {
      const response = await api.post<IWebhookDocument>(this.baseUrl, webhook);
      return response.data;
    } catch (error: unknown) {
      console.error("Erro ao criar webhook:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Atualiza um webhook existente
   * @param id ID do webhook
   * @param dados Dados a serem atualizados
   * @returns Webhook atualizado
   */
  async atualizar(
    id: string,
    dados: Partial<IWebhookDocument>
  ): Promise<IWebhookDocument> {
    try {
      const response = await api.put<IWebhookDocument>(
        `${this.baseUrl}/${id}`,
        dados
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Erro ao atualizar webhook:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Busca um webhook pelo ID
   * @param id ID do webhook
   * @returns Webhook encontrado ou null
   */
  async buscarPorId(id: string): Promise<IWebhookDocument | null> {
    try {
      console.log(`Buscando webhook com ID: ${id}, URL: ${this.baseUrl}/${id}`);
      
      const response = await api.get<IWebhookDocument | ApiResponse<IWebhookDocument>>(`${this.baseUrl}/${id}`);
      
      console.log("Resposta da API para buscarPorId:", response.status, response.statusText);
      console.log("Dados recebidos:", JSON.stringify(response.data, null, 2));
      
      // Verificar o formato da resposta
      let webhook: IWebhookDocument | null = null;
      
      // Se a resposta estiver no formato { success: true, data: webhook }
      if (response.data && 'success' in response.data && response.data.data) {
        webhook = this.parseWebhookDates(response.data.data);
      } else {
        webhook = this.parseWebhookDates(response.data as IWebhookDocument);
      }
      
      console.log("Webhook processado:", webhook);
      
      if (!webhook) {
        console.error("Webhook nulo após processamento");
        throw new Error("Webhook não encontrado ou formato inválido");
      }
      
      return webhook;
    } catch (error: unknown) {
      console.error("Erro ao buscar webhook:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Converte campos de data do webhook para objetos Date
   * @param webhook Webhook a ser processado
   * @returns Webhook com datas processadas ou null
   */
  private parseWebhookDates(webhook: IWebhookDocument | null): IWebhookDocument | null {
    if (!webhook) return null;
    
    // Criar uma cópia para não modificar o objeto original
    const result = { ...webhook };
    
    // Converter campos de data para objetos Date
    if (result.criadoEm && typeof result.criadoEm === 'string') {
      result.criadoEm = new Date(result.criadoEm);
    }
    
    if (result.atualizadoEm && typeof result.atualizadoEm === 'string') {
      result.atualizadoEm = new Date(result.atualizadoEm);
    }
    
    if (result.ultimoAcionadoEm && typeof result.ultimoAcionadoEm === 'string') {
      result.ultimoAcionadoEm = new Date(result.ultimoAcionadoEm);
    }
    
    return result;
  }

  /**
   * Exclui um webhook
   * @param id ID do webhook
   * @returns true se excluído com sucesso
   */
  async excluir(id: string): Promise<boolean> {
    try {
      await api.delete(`${this.baseUrl}/${id}`);
      return true;
    } catch (error: unknown) {
      console.error("Erro ao excluir webhook:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Busca webhooks por projeto
   * @param projetoId ID do projeto
   * @returns Lista de webhooks
   */
  async buscarPorProjeto(
    projetoId: string
  ): Promise<IWebhookDocument[]> {
    try {
      console.log(`🔍 Buscando webhooks para projeto: ${projetoId}`);
      
      // Verificar inconsistência de projeto
      const currentUser = authService.getCurrentUser();
      if (currentUser?.projectId && currentUser.projectId !== projetoId) {
        console.warn(`⚠️ Inconsistência de projeto: solicitado ${projetoId}, usuário ${currentUser.projectId}`);
      }
      
      const response = await api.get<IWebhookDocument[] | ApiResponse<IWebhookDocument[]>>(
        `${this.baseUrl}/projeto/${projetoId}`
      );

      // Verificar o formato da resposta
      let webhooks: IWebhookDocument[] = [];

      // Se a resposta estiver no formato { success: true, data: webhooks[] }
      if (response.data && 'success' in response.data && Array.isArray(response.data.data)) {
        webhooks = response.data.data
          .map((webhook: any) => this.parseWebhookDates(webhook))
          .filter((webhook: any): webhook is IWebhookDocument => webhook !== null);
      } else if (Array.isArray(response.data)) {
        webhooks = response.data
          .map((webhook: any) => this.parseWebhookDates(webhook))
          .filter((webhook: any): webhook is IWebhookDocument => webhook !== null);
      }

      console.log("Webhooks recebidos por projeto:", response.data);
      console.log("Webhooks processados:", webhooks);

      return webhooks;
    } catch (error: unknown) {
      console.error("Erro ao buscar webhooks por projeto:", error);
      this.logAxiosError(error);
      
      // Se erro 403, pode ser inconsistência de projeto - sugerir refresh
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.status === 403) {
          console.warn('🔄 Erro 403: Pode ser necessário atualizar token com projeto correto');
        }
      }
      
      throw error;
    }
  }

  /**
   * Busca webhooks por organização (método mantido para compatibilidade)
   * @param organizacaoId ID da organização
   * @returns Lista de webhooks
   * @deprecated Use buscarPorProjeto
   */
  async buscarPorOrganizacao(
    organizacaoId: string
  ): Promise<IWebhookDocument[]> {
    return this.buscarPorProjeto(organizacaoId);
  }

  /**
   * Busca webhooks por agente
   * @param agenteId ID do agente
   * @returns Lista de webhooks
   */
  async buscarPorAgente(agenteId: string): Promise<IWebhookDocument[]> {
    try {
      const response = await api.get<IWebhookDocument[]>(
        `${this.baseUrl}/agente/${agenteId}`
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Erro ao buscar webhooks por agente:", error);
      this.logAxiosError(error);
      throw error;
    }
  }


  /**
   * Busca entregas de um webhook
   * @param webhookId ID do webhook
   * @returns Lista de entregas
   */
  async buscarEntregas(webhookId: string): Promise<WebhookEntrega[]> {
    try {
      const response = await api.get<WebhookEntrega[] | ApiResponse<WebhookEntrega[]>>(
        `${this.baseUrl}/${webhookId}/entregas`
      );

      let entregas: WebhookEntrega[] = [];

      // Verificar formato da resposta
      if (response.data && 'success' in response.data && Array.isArray(response.data.data)) {
        entregas = response.data.data.map((entrega: any) => this.parseEntregaDates(entrega));
      } else if (Array.isArray(response.data)) {
        entregas = response.data.map((entrega: any) => this.parseEntregaDates(entrega));
      }

      console.log("Entregas recebidas:", response.data);
      console.log("Entregas processadas:", entregas);

      return entregas;
    } catch (error: unknown) {
      console.error("Erro ao buscar entregas do webhook:", error);
      this.logAxiosError(error);
      // Em caso de erro, retornar array vazio para evitar quebras na interface
      return [];
    }
  }

  /**
   * Converte campos de data da entrega para objetos Date
   */
  private parseEntregaDates(entrega: WebhookEntrega): WebhookEntrega {
    if (!entrega) return entrega;

    const result = { ...entrega };

    if (result.criadoEm && typeof result.criadoEm === 'string') {
      result.criadoEm = new Date(result.criadoEm);
    }

    if (result.concluidoEm && typeof result.concluidoEm === 'string') {
      result.concluidoEm = new Date(result.concluidoEm);
    }

    return result;
  }

  /**
   * Registra uma entrega de webhook
   * @param entrega Dados da entrega
   * @returns Entrega registrada
   */
  async registrarEntrega(entrega: WebhookEntrega): Promise<WebhookEntrega> {
    try {
      const response = await api.post<WebhookEntrega | ApiResponse<WebhookEntrega>>(
        `${this.baseUrl}/entregas`,
        entrega
      );

      let result: WebhookEntrega;

      if (response.data && 'success' in response.data && response.data.data) {
        result = this.parseEntregaDates(response.data.data);
      } else {
        result = this.parseEntregaDates(response.data as WebhookEntrega);
      }

      return result;
    } catch (error: unknown) {
      console.error("Erro ao registrar entrega de webhook:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Atualiza uma entrega de webhook
   * @param id ID da entrega
   * @param dados Dados a serem atualizados
   * @returns Entrega atualizada
   */
  async atualizarEntrega(
    id: string,
    dados: Partial<WebhookEntrega>
  ): Promise<WebhookEntrega> {
    try {
      const response = await api.put<WebhookEntrega>(
        `${this.baseUrl}/entregas/${id}`,
        dados
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Erro ao atualizar entrega de webhook:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Verifica autorização de um webhook
   * @param webhookId ID do webhook
   * @param chaveApi Chave de API
   * @returns true se autorizado
   */
  async verificarAutorizacao(
    webhookId: string,
    chaveApi: string
  ): Promise<boolean> {
    try {
      const response = await api.post<{ autorizado: boolean }>(
        `${this.baseUrl}/${webhookId}/autorizar`,
        { chaveApi }
      );
      return response.data.autorizado;
    } catch (error: unknown) {
      console.error("Erro ao verificar autorização de webhook:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Cria um vínculo entre webhook e agente
   * @param vinculo Dados do vínculo
   * @returns Vínculo criado
   */
  async criarVinculoAgente(
    vinculo: WebhookAgenteVinculo
  ): Promise<WebhookAgenteVinculo> {
    try {
      const response = await api.post<WebhookAgenteVinculo>(
        `${this.baseUrl}/vinculos`,
        vinculo
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Erro ao criar vínculo webhook-agente:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Atualiza um vínculo entre webhook e agente
   * @param id ID do vínculo
   * @param dados Dados a serem atualizados
   * @returns Vínculo atualizado
   */
  async atualizarVinculoAgente(
    id: string,
    dados: Partial<WebhookAgenteVinculo>
  ): Promise<WebhookAgenteVinculo> {
    try {
      const response = await api.put<WebhookAgenteVinculo>(
        `${this.baseUrl}/vinculos/${id}`,
        dados
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Erro ao atualizar vínculo webhook-agente:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Exclui um vínculo entre webhook e agente
   * @param id ID do vínculo
   * @returns true se excluído com sucesso
   */
  async excluirVinculoAgente(id: string): Promise<boolean> {
    try {
      await api.delete(`${this.baseUrl}/vinculos/${id}`);
      return true;
    } catch (error: unknown) {
      console.error("Erro ao excluir vínculo webhook-agente:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Busca um vínculo entre webhook e agente
   * @param webhookId ID do webhook
   * @returns Vínculo encontrado ou null
   */
  async buscarVinculoAgente(
    webhookId: string
  ): Promise<WebhookAgenteVinculo | null> {
    try {
      const response = await api.get<WebhookAgenteVinculo>(
        `${this.baseUrl}/vinculos/${webhookId}`
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Erro ao buscar vínculo webhook-agente:", error);
      this.logAxiosError(error);
      throw error;
    }
  }

  /**
   * Busca vínculos por agente
   * @param agenteId ID do agente
   * @returns Lista de vínculos
   */
  async buscarVinculosPorAgente(
    agenteId: string
  ): Promise<WebhookAgenteVinculo[]> {
    try {
      const response = await api.get<WebhookAgenteVinculo[] | ApiResponse<WebhookAgenteVinculo[]>>(
        `${this.baseUrl}/vinculos/agente/${agenteId}`
      );

      let vinculos: WebhookAgenteVinculo[] = [];

      // Verificar formato da resposta
      if (response.data && 'success' in response.data && Array.isArray(response.data.data)) {
        vinculos = response.data.data.map((vinculo: any) => this.parseVinculoDates(vinculo));
      } else if (Array.isArray(response.data)) {
        vinculos = response.data.map((vinculo: any) => this.parseVinculoDates(vinculo));
      }

      console.log("Vínculos recebidos:", response.data);
      console.log("Vínculos processados:", vinculos);

      return vinculos;
    } catch (error: unknown) {
      console.error("Erro ao buscar vínculos por agente:", error);
      this.logAxiosError(error);
      // Em caso de erro, retornar array vazio para evitar quebras na interface
      return [];
    }
  }

  /**
   * Converte campos de data do vínculo para objetos Date
   */
  private parseVinculoDates(vinculo: WebhookAgenteVinculo): WebhookAgenteVinculo {
    if (!vinculo) return vinculo;

    const result = { ...vinculo };

    if (result.criadoEm && typeof result.criadoEm === 'string') {
      result.criadoEm = new Date(result.criadoEm);
    }

    if (result.atualizadoEm && typeof result.atualizadoEm === 'string') {
      result.atualizadoEm = new Date(result.atualizadoEm);
    }

    return result;
  }

  /**
   * Helper method to log axios errors consistently
   */
  private logAxiosError(error: unknown): void {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        console.error("Detalhes do erro da API:", {
          status: axiosError.response.status,
          statusText: axiosError.response.statusText,
          data: axiosError.response.data
        });
      }
    }
  }
}